import { useTranslation } from 'react-i18next';
import { LabReferenceList } from '@/components/lab/LabObjectLink';
import { LabProvisionalStar } from '@/components/lab/LabProvisionalStar';
import { LabReferenceInterval } from '@/components/lab/LabReferenceInterval';
import { LabValidatedValue } from '@/components/lab/LabValidatedValue';
import {
  displayResultLongText,
  displayResultValue,
  displayVersionValue,
  formatLabDateTime,
  statusLabel,
} from '@/lib/lab/labDisplay';
import type { LabObjectLink, LabResultRow, LabResultVersion } from '@/lib/lab/labTypes';

export function LabResultRowView({
  row,
  versions,
  clinicId = '',
  objectLinks = [],
  consentGiven,
  replacedBy,
}: {
  row: LabResultRow;
  versions?: LabResultVersion[];
  clinicId?: string;
  objectLinks?: LabObjectLink[];
  consentGiven?: boolean | null;
  replacedBy?: string | null;
}) {
  const { t } = useTranslation();
  const value = displayResultValue(row);
  const longText = displayResultLongText(row);
  const corrected = row.is_corrected || row.result_status_code === 'svar_rettet';
  const refused = consentGiven === false;
  return (
    <tr>
      <td>
        <div className="font-medium">
          {row.analysis_short_name || row.analysis_code || '—'}
          <LabProvisionalStar code={row.result_status_code} />
        </div>
        <div className="text-ds-muted">{row.analysis_complete_name}</div>
        {row.analysis_code ? (
          <div className="text-xs text-ds-muted">
            {row.analysis_code}
            {row.analysis_code_type ? ` · ${row.analysis_code_type}` : ''}
          </div>
        ) : null}
        {refused ? <span className="lab-chip lab-chip--warn mt-s2">{t('patients.lab.consent_refused')}</span> : null}
      </td>
      <td>
        <LabValidatedValue validation={row.result_validation} interval={row} corrected={corrected}>
          {value}
          {corrected ? ' R' : ''}
        </LabValidatedValue>
        {row.unit ? <span className="ml-1 text-ds-muted">{row.unit}</span> : null}
        {row.result_status_code ? (
          <div className="text-xs text-ds-muted">{statusLabel(row.result_status_code)}</div>
        ) : null}
        {replacedBy ? <div className="mt-1 text-xs text-ds-muted">{replacedBy}</div> : null}
        {longText ? <div className="lab-result-text mt-1">{longText}</div> : null}
        <LabReferenceList
          refs={row.reference_links}
          clinicId={clinicId}
          reportId={row.report_id}
          links={objectLinks}
          consentGiven={consentGiven}
        />
      </td>
      <td>
        <LabReferenceInterval
          reference_interval_type={row.reference_interval_type}
          reference_lower={row.reference_lower}
          reference_upper={row.reference_upper}
          reference_interval_text={row.reference_interval_text}
        />
      </td>
      <td>
        {row.producer_identifier || '—'}
        {row.producer_identifier_code ? (
          <div className="text-xs text-ds-muted">{row.producer_identifier_code}</div>
        ) : null}
        {row.to_lab_identifier ? (
          <div className="text-xs text-ds-muted">Til lab: {row.to_lab_identifier}</div>
        ) : null}
      </td>
      <td>
        {formatLabDateTime(row.results_datetime)}
        {versions && versions.length > 0 ? (
          <ol className="mt-s2 space-y-1 text-xs text-ds-muted">
            {versions.map((v) => (
              <li key={v.id}>
                {formatLabDateTime(v.results_datetime)} · {displayVersionValue(v)}
                {v.producer_identifier ? ` · ${v.producer_identifier}` : ''}
                {v.result_status_code === 'svar_rettet' ? ' R' : ''}
              </li>
            ))}
          </ol>
        ) : null}
      </td>
    </tr>
  );
}
